import useReportSort from '../../components/reports/useReportSort';
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiShield,
} from "react-icons/fi";

import ReportFilterModal from "../../components/reports/ReportFilterModal";
import SearchableSelect from "../../components/reports/SearchableSelect";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const initialFilters = () => ({
  storeId: "",
  brand: "",
  group: "",
  search: "",
});

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const quantity = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const unique = (rows, key) =>
  [...new Set(rows.map((row) => row[key]).filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b)),
  );

function downloadFile(blob, name) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

function Field({ label, children }) {
  return (
    <label className="block text-sm font-semibold text-slate-700">
      <span className="mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export default function StockReportPage() {
  const { accessToken, user } = useAuth();
  const canGenerate = hasPermission(user, "reports.stock.generate");
  const canExport = hasPermission(user, "reports.stock.export");
  const [stores, setStores] = useState([]);
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const loadStores = useCallback(async () => {
    try {
      const response = await apiRequest("/business-reports/stock/stores", {
        accessToken,
      });
      setStores(response || []);
    } catch (requestError) {
      setError(requestError.message || "Could not load stores.");
    }
  }, [accessToken]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const rows = report?.rows || [];
  const brands = useMemo(() => unique(rows, "companyName"), [rows]);
  const groups = useMemo(() => unique(rows, "groupName"), [rows]);

  const visibleRows = useMemo(() => {
    const search = appliedFilters.search.trim().toLowerCase();

    return rows.filter((row) => {
      const searchableValues = [
        row.itemName,
        row.itemCode,
        row.sku,
        row.productCode,
        row.lotCode,
        row.shadeName,
        row.godownName,
      ];

      return (
        (!appliedFilters.brand || row.companyName === appliedFilters.brand) &&
        (!appliedFilters.group || row.groupName === appliedFilters.group) &&
        (!search ||
          searchableValues
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(search)))
      );
    });
  }, [appliedFilters, rows]);

  const summary = useMemo(
    () =>
      visibleRows.reduce(
        (total, row) => ({
          lots: total.lots + 1,
          availableQuantity:
            total.availableQuantity + Number(row.availableQuantity || 0),
          purchaseValue: total.purchaseValue + Number(row.purchaseValue || 0),
          mrpValue: total.mrpValue + Number(row.mrpValue || 0),
        }),
        { lots: 0, availableQuantity: 0, purchaseValue: 0, mrpValue: 0 },
      ),
    [visibleRows],
  );

  const updateFilter = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const generate = async () => {
    if (!canGenerate) return;

    setLoading(true);
    setError("");

    try {
      const nextReport = await apiRequest("/business-reports/stock/generate", {
        method: "POST",
        body: {
          ...(draftFilters.storeId ? { storeId: draftFilters.storeId } : {}),
        },
        accessToken,
      });
      setReport(nextReport);
      setAppliedFilters({ ...draftFilters });
      setShowFilters(false);
    } catch (requestError) {
      setReport(null);
      setError(requestError.message || "Could not generate the stock report.");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!report || !canExport) return;

    setExporting(true);
    setError("");

    try {
      const params = new URLSearchParams({ format: "XLSX" });
      if (appliedFilters.storeId) params.set("storeId", appliedFilters.storeId);

      const response = await apiRequest(
        `/business-reports/stock/export?${params.toString()}`,
        { accessToken, rawResponse: true },
      );
      const name =
        response.headers
          .get("content-disposition")
          ?.match(/filename="?([^";]+)"?/)?.[1] ||
        "charms-current-stock-report.xlsx";
      downloadFile(await response.blob(), name);
    } catch (requestError) {
      setError(requestError.message || "Could not export the stock report.");
    } finally {
      setExporting(false);
    }
  };

  const openFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(true);
  };

  const cancelFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(false);
  };

  const summaryCards = [
    ["Lots in stock", summary.lots, "text-primary-800"],
    ["Available quantity", quantity(summary.availableQuantity), "text-sky-700"],
    ["Total purchase value", money(summary.purchaseValue), "text-amber-700"],
    ["Total MRP value", money(summary.mrpValue), "text-emerald-700"],
  ];

  const columns = [
    "Company name",
    "Item name",
    "Group name",
    "Pack / size",
    "Available qty",
    "Purchase rate",
    "Purchase value",
    "MRP",
    "MRP value",
    "Lot code",
    "Shade name",
    "Godown name",
    "Item code / barcode",
  ];

  return (
    <div className="business-report space-y-5">
      <section className="border-b border-slate-200 bg-white px-1 pb-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[.18em] text-primary-800">
              <FiPackage /> Business reports
            </div>
            <h1 className="text-3xl font-bold text-slate-950">Stock Report</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-500">
              Current available inventory by product, barcode and lot. This is a
              live stock snapshot, not a sales report.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating…" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              disabled={!report || !canExport || exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}
              Export Excel
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {!canGenerate && (
        <p className="flex gap-2 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
          <FiShield className="mt-0.5 shrink-0" />
          You do not have permission to generate this report.
        </p>
      )}

      {!report ? (
        <section className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-20 text-center">
          <FiFileText className="mx-auto mb-4 text-4xl text-primary-300" />
          <h2 className="text-xl font-bold text-slate-900">
            No stock snapshot generated yet
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Select a godown or product filter, then choose Generate.
          </p>
        </section>
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {summaryCards.map(([label, value, color]) => (
              <div
                key={label}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <p className="text-sm text-slate-500">{label}</p>
                <p className={`mt-2 text-2xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </section>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">
                  Current inventory by lot
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {visibleRows.length.toLocaleString("en-IN")} in-stock lot
                  record(s). Values include only the quantity currently
                  available.
                </p>
              </div>
              <p className="text-xs font-medium text-slate-500">
                Snapshot generated{" "}
                {new Date(report.generatedAt).toLocaleString("en-IN")}
              </p>
            </div>
            <div className="overflow-x-auto">
                  {sortControls}
              <table className="min-w-[1800px] w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                  <tr>
                    {columns.map((header) => (
                      <th
                        key={header}
                        className="whitespace-nowrap border-b border-slate-200 px-4 py-3"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr
                      key={`${row.lotCode}-${row.itemCode}`}
                      className="border-b border-slate-100 text-slate-700 hover:bg-primary-50/30"
                    >
                      <td className="px-4 py-3">{row.companyName || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {row.itemName}
                        <p className="mt-1 text-xs font-normal text-slate-500">
                          {row.productCode || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-3">{row.groupName || "—"}</td>
                      <td className="px-4 py-3">{row.packSize || "—"}</td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {quantity(row.availableQuantity)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {money(row.purchaseRate)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {money(row.purchaseValue)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {row.mrp === null ? "—" : money(row.mrp)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                        {money(row.mrpValue)}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.lotCode}
                      </td>
                      <td className="px-4 py-3">{row.shadeName || "—"}</td>
                      <td className="px-4 py-3">{row.godownName}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.itemCode}
                      </td>
                    </tr>
                  ))}
                  {!visibleRows.length && (
                    <tr>
                      <td
                        colSpan="13"
                        className="px-4 py-16 text-center text-sm text-slate-500"
                      >
                        No in-stock products match the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <ReportFilterModal
        open={showFilters}
        title="Stock report filters"
        description="Choose a godown, brand, item group or product. Generate applies the filters and closes this window."
        loading={loading}
        onApply={generate}
        onClose={cancelFilters}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Godown / Store">
            <SearchableSelect
              value={draftFilters.storeId}
              onChange={(value) => updateFilter("storeId", value)}
              placeholder="All stores"
              options={stores.map((store) => ({
                value: store.id,
                label: `${store.name} (${store.code})`,
              }))}
            />
          </Field>
          <Field label="Company / Brand">
            <SearchableSelect
              value={draftFilters.brand}
              onChange={(value) => updateFilter("brand", value)}
              placeholder="All brands"
              options={brands}
            />
          </Field>
          <Field label="Item group">
            <SearchableSelect
              value={draftFilters.group}
              onChange={(value) => updateFilter("group", value)}
              placeholder="All item groups"
              options={groups}
            />
          </Field>
          <Field label="Search product, barcode or lot">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={draftFilters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                className="report-input pl-9"
                placeholder="Product, item code or lot code"
              />
            </div>
          </Field>
        </div>
      </ReportFilterModal>
    </div>
  );
}
